import React, { Component, createRef } from 'react';
import { ThemeContext } from '../ThemeContext';
import logo from "../assets/images/logo.png";

/*
  Valid link patterns:
  - https://staging.pay.payswitch.africa/pay/:uuid
  - https://pay.payswitch.africa/pay/:uuid
  - https://staging.pay.payswitch.africa/payout/:uuid/claim
  - https://pay.payswitch.africa/payout/:uuid/claim
*/
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HOSTS   = ['pay.payswitch.africa', 'staging.pay.payswitch.africa'];

function parseLink(raw) {
    const str = (raw || '').trim();
    if (!str) return { valid: false, error: '' };
    let url;
    try { url = new URL(str); }
    catch { return { valid: false, error: 'Please enter a valid URL.' }; }

    if (!HOSTS.includes(url.hostname)) {
        return { valid: false, error: 'Invalid link — must be a payswitch.africa payment or payout link.' };
    }

    // /pay/:uuid
    const payMatch = url.pathname.match(/^\/pay\/([^/]+)\/?$/);
    if (payMatch) {
        if (!UUID_RE.test(payMatch[1])) return { valid: false, error: 'Invalid payment link — the ID format is incorrect.' };
        return { valid: true, type: 'pay', path: `/pay/${payMatch[1]}` };
    }

    // /payout/:uuid/claim
    const payoutMatch = url.pathname.match(/^\/payout\/([^/]+)\/claim\/?$/);
    if (payoutMatch) {
        if (!UUID_RE.test(payoutMatch[1])) return { valid: false, error: 'Invalid payout link — the ID format is incorrect.' };
        return { valid: true, type: 'payout', path: `/payout/${payoutMatch[1]}/claim` };
    }

    // path starts with /pay but doesn't match
    if (url.pathname.startsWith('/pay')) return { valid: false, error: 'Invalid payment link format.' };
    if (url.pathname.startsWith('/payout')) return { valid: false, error: 'Invalid payout link format.' };

    return { valid: false, error: 'Unrecognised Payswitch link format.' };
}

class Welcome extends Component {
    static contextType = ThemeContext;

    state = {
        linkValue: '',
        error: '',
        qrError: '',
        qrSuccess: '',
        isDragging: false,
    };

    fileInputRef = createRef();

    handleLinkChange = (e) => {
        this.setState({ linkValue: e.target.value, error: '', qrError: '', qrSuccess: '' });
    };

    handleSubmit = (e) => {
        e && e.preventDefault();
        const { linkValue } = this.state;
        const result = parseLink(linkValue);
        if (!result.valid) {
            this.setState({ error: result.error || 'Please enter a payment or payout link.' });
            return;
        }
        window.location.href = result.path;
    };

    handleKeyDown = (e) => {
        if (e.key === 'Enter') this.handleSubmit();
    };

    decodeQR = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const img = new window.Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width  = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                /* global jsQR */
                if (typeof jsQR === 'undefined') {
                    this.setState({ qrError: 'QR decoder not loaded. Please paste the link manually.' });
                    return;
                }
                const code = jsQR(imageData.data, imageData.width, imageData.height);
                if (!code) {
                    this.setState({ qrError: 'No QR code found in this image. Try a clearer photo.', qrSuccess: '' });
                    return;
                }
                const result = parseLink(code.data);
                if (!result.valid) {
                    this.setState({ qrError: result.error || 'QR code does not contain a valid Payswitch link.', qrSuccess: '' });
                    return;
                }
                this.setState({ qrSuccess: `QR decoded: ${code.data}`, qrError: '', linkValue: code.data, error: '' });
                // Auto-navigate after short delay so user sees feedback
                setTimeout(() => { window.location.href = result.path; }, 900);
            };
            img.src = ev.target.result;
        };
        reader.readAsDataURL(file);
    };

    handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) this.decodeQR(file);
        e.target.value = '';
    };

    handleDrop = (e) => {
        e.preventDefault();
        this.setState({ isDragging: false });
        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith('image/')) this.decodeQR(file);
        else this.setState({ qrError: 'Please drop an image file.' });
    };

    handleDragOver  = (e) => { e.preventDefault(); this.setState({ isDragging: true }); };
    handleDragLeave = ()  => { this.setState({ isDragging: false }); };

    render() {
        const { linkValue, error, qrError, qrSuccess, isDragging } = this.state;

        return (
            <div className="ps-welcome-wrap">
                <div className="ps-welcome-card">
                    {/* Top decoration */}
                    <div className="ps-welcome-top-bar" />

                    <div className="ps-welcome-body">
                        {/* Headline */}
                        <div className="ps-welcome-hero">
                            <div className="ps-welcome-icon-ring">
                                {/*<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">*/}
                                {/*    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>*/}
                                {/*    <line x1="1" y1="10" x2="23" y2="10"/>*/}
                                {/*</svg>*/}
                                <img src={logo} alt="Payswitch" />
                            </div>
                            <h1 className="ps-welcome-title">Welcome to Payswitch Pay</h1>
                            <p className="ps-welcome-subtitle">
                                Enter your payment link below, or scan a QR code to get started instantly.
                            </p>
                        </div>

                        {/* Link input */}
                        <div className="ps-welcome-section">
                            <label className="ps-label">Payment or Payout Link</label>
                            <div className="ps-welcome-input-row">
                                <div className="ps-welcome-input-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                                    </svg>
                                </div>
                                <input
                                    className={`ps-input ps-welcome-input${error ? ' is-invalid' : ''}`}
                                    type="url"
                                    placeholder="https://pay.payswitch.africa/pay/..."
                                    value={linkValue}
                                    onChange={this.handleLinkChange}
                                    onKeyDown={this.handleKeyDown}
                                    autoComplete="off"
                                    spellCheck={false}
                                />
                            </div>
                            {error && (
                                <div className="ps-welcome-error">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                                    </svg>
                                    {error}
                                </div>
                            )}

                            <button className="ps-btn-primary ps-welcome-btn" onClick={this.handleSubmit}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                </svg>
                                Proceed to Payment
                            </button>
                        </div>

                        {/* Divider */}
                        <div className="ps-welcome-divider">
                            <span>or scan QR code</span>
                        </div>

                        {/* QR drop zone */}
                        <div
                            className={`ps-qr-zone${isDragging ? ' dragging' : ''}`}
                            onClick={() => this.fileInputRef.current.click()}
                            onDrop={this.handleDrop}
                            onDragOver={this.handleDragOver}
                            onDragLeave={this.handleDragLeave}
                        >
                            <input
                                ref={this.fileInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={this.handleFileChange}
                            />
                            <div className="ps-qr-icon">
                                {/* QR code icon */}
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/>
                                    <rect x="5" y="5" width="3" height="3" rx="0.5" fill="currentColor" stroke="none"/>
                                    <rect x="16" y="5" width="3" height="3" rx="0.5" fill="currentColor" stroke="none"/>
                                    <rect x="5" y="16" width="3" height="3" rx="0.5" fill="currentColor" stroke="none"/>
                                    <path d="M14 14h2v2h-2z M18 14h2v2h-2z M14 18h2v2h-2z M18 18h2v2h-2z" fill="currentColor" stroke="none"/>
                                </svg>
                            </div>
                            <p className="ps-qr-label">
                                {isDragging ? 'Drop your QR code image here' : 'Click or drag & drop a QR code image'}
                            </p>
                            <p className="ps-qr-hint">Supports PNG, JPG, WEBP</p>
                        </div>

                        {/* QR feedback */}
                        {qrError && (
                            <div className="ps-welcome-error" style={{ marginTop: '0.75rem' }}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                                </svg>
                                {qrError}
                            </div>
                        )}
                        {qrSuccess && (
                            <div className="ps-welcome-success">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                                    <polyline points="20 6 9 17 4 12"/>
                                </svg>
                                {qrSuccess}
                            </div>
                        )}

                        {/* Supported format hint */}
                        <div className="ps-welcome-formats">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                                <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                            </svg>
                            Links must be from <strong>Payswitch</strong>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default Welcome;
